import Image from "next/image";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { TContact } from "@/pages/api/contact";
import { TSns } from "@/pages/api/sns";
import { fetchApiData } from "@/lib/api";
import { API_ENDPOINTS } from "@/lib/constants";
import { media } from "@/styles/media";
import { footerMenu } from "@/lib/data/menu";

export default function Footer() {
  const [contact, setContact] = useState<TContact[]>([]);
  const [sns, setSns] = useState<TSns[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    async function init() {
      const [newContact, newSns] = await Promise.all([
        fetchApiData<TContact>(API_ENDPOINTS.CONTACT),
        fetchApiData<TSns>(API_ENDPOINTS.SNS),
      ]);

      setContact(newContact);
      setSns(newSns);
    }
    init();
  }, []);

  return (
    <FooterWrapper>
      <ItemWrapper>
        {footerMenu.map((section, sectionIndex) => {
          return (
            <Item key={sectionIndex}>
              <H2>{t(section.title)}</H2>
              <MenuList>
                {section.items.map((item, itemIndex) => {
                  return (
                    <Li key={itemIndex}>
                      <A href={item.href} target="_self">
                        {t(item.label)}
                      </A>
                    </Li>
                  );
                })}
              </MenuList>
            </Item>
          );
        })}
        <AdditionalItem>
          <AdditionalWrapper>
            <MenuList>
              {contact.map((item, index) => {
                return (
                  <Li key={index}>
                    <A href={item.uri} target="_blank">
                      {t(item.label)}
                    </A>
                  </Li>
                );
              })}
            </MenuList>
            <MenuList>
              {sns.map((item, index) => {
                return (
                  <Li key={index}>
                    <Image src={item.icon} alt="sns" width={20} height={20} />
                    <A href={item.uri} target="_blank">
                      {t(item.label)}
                    </A>
                  </Li>
                );
              })}
            </MenuList>
          </AdditionalWrapper>
        </AdditionalItem>
      </ItemWrapper>
      <OuterFooter>
        <Copy>© {t("main.footer.inbody-asia")}</Copy>
      </OuterFooter>
    </FooterWrapper>
  );
}

const FooterWrapper = styled.div`
  padding: 32px;
`;
const ItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 32px 32px 50px 32px;
  box-sizing: border-box;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.background.white};

  ${media.tablet`
    flex-direction: column;
    gap: 40px;
  `}
`;
const Item = styled.div`
  width: 19.5%;

  ${media.tablet`
    width: 100%;
  `}
`;
const AdditionalItem = styled(Item)`
  width: 35%;
`;
const AdditionalWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  > ul:first-child {
    width: 230px;
    gap: 16px;
  }
  > ul:last-child {
    min-width: 105px;
  }

  ${media.tablet`
    flex-direction: column;
    gap: 40px;
  `}
`;
const H2 = styled.h2`
  display: flex;
  align-items: center;
  height: 32px;
  margin-bottom: 20px;
  font-size: 1.75rem;
  color: #971b2f;
`;
const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Li = styled.li`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 25px;
  list-style: none;
`;
const A = styled.a`
  min-width: 70px;
  line-height: 25px;
  transition: all 0.3s;
  &:hover {
    transition: all 0.3s;
    color: #971b2f;
  }
`;
const OuterFooter = styled.div`
  border-radius: 10px;
  background-color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding: 20px 28px;
`;

const Copy = styled.p`
  display: flex;
  align-items: center;
  height: 22px;
  font-size: 0.8rem;
  color: grey;
`;
